import { useEffect, useRef } from 'react';
import { track } from '../lib/analytics';

const VIEWED = new Set<string>();
const ENGAGED = new Set<string>();

const ENGAGEMENT_MS = 3000;

/**
 * Fire `section_view` once per section and `section_engaged` once after
 * the section has been in the viewport for >= 3 seconds.
 */
export function useSectionView(sectionName: string) {
  const ref = useRef<HTMLElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!VIEWED.has(sectionName)) {
              VIEWED.add(sectionName);
              track('section_view', { section_name: sectionName, sequence_index: VIEWED.size });
            }
            visibleRef.current = true;
            timerRef.current = setTimeout(() => {
              if (visibleRef.current && !ENGAGED.has(sectionName)) {
                ENGAGED.add(sectionName);
                track('section_engaged', { section_name: sectionName, duration_ms: ENGAGEMENT_MS });
              }
            }, ENGAGEMENT_MS);
          } else {
            visibleRef.current = false;
            if (timerRef.current) {
              clearTimeout(timerRef.current);
              timerRef.current = null;
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sectionName]);

  return ref;
}

// Scroll depth tracking (25/50/75/90/100) — fire each once.
let scrollDepthHookInstalled = false;
const FIRED_DEPTHS = new Set<number>();
const DEPTHS = [25, 50, 75, 90, 100];

export function useScrollDepth() {
  useEffect(() => {
    if (scrollDepthHookInstalled) return;
    scrollDepthHookInstalled = true;

    const onScroll = () => {
      const docH = document.documentElement.scrollHeight;
      const winH = window.innerHeight;
      const scrollY = window.scrollY;
      if (docH <= winH) return;
      const pct = Math.round(((scrollY + winH) / docH) * 100);

      for (const d of DEPTHS) {
        if (pct >= d && !FIRED_DEPTHS.has(d)) {
          FIRED_DEPTHS.add(d);
          track('scroll_depth', { scroll_percent: d });
        }
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      scrollDepthHookInstalled = false;
    };
  }, []);
}
